import React from "react";
import { AllocationType } from "../../types/api/allocations";
import { ACTION_DISPLAY_TYPES, BANK_ACCOUNT_FLOW_TYPES, BANK_ACCOUNT_STATUS } from "../../constants/enums";
import { useDispatch } from "react-redux";
import { ROUTES } from "../../constants/clientRoutes";
import { UpdateBankAcccountParams } from "../../actions/bankAccounts";
import { useHistory } from "react-router-dom";

import BankAccountToolTip from "../ODT/BankAccountToolTip";
import i18n from "../../i18n/config";
import Action from "../Common/Action";

const AllocationTableRow = ({ allocation, showStatusCol, isIndexPage, handleInfoTipModal }: { allocation: AllocationType, showStatusCol: boolean, isIndexPage: boolean, handleInfoTipModal: (title: string, content: string) => void }) => {
  const { accountName, name, productCurrency, destinationCurrency, destinationCountry, wireFee, selected, selectedForFreeTransfer } = allocation;
  const dispatch = useDispatch();
  const history = useHistory();

  /**
   * if allocation has moreInfo property populated, return a tooltip, otherwise return status
   * @returns Status of the allocation
   */
  const getStatus = () => {
    if (showStatusCol) {
      if (allocation.moreInfo) {
        return <BankAccountToolTip moreInfo={allocation.moreInfo} handleInfoTipModal={handleInfoTipModal} />
      } else if (allocation.fixed > 0 || allocation.percentage > 0 || allocation.getsRemainder) {
        return <p className="bold">{i18n.t("PAGES.ALLOCATIONS.INDEX.ALLOCATION_STATUS.ACTIVE")}</p>;
      } else {
        return <p>{i18n.t("PAGES.ALLOCATIONS.INDEX.ALLOCATION_STATUS.AVAILABLE")}</p>;
      }
    }
  }

  const handleEditBank = () => {
    dispatch(UpdateBankAcccountParams(BANK_ACCOUNT_FLOW_TYPES.EDIT, allocation.id));
    history.push(`${ROUTES.BANKS_ACCOUNTS.BENEFICIARY_DETAILS}?destinationCountryId=${allocation.destinationCountry.id}&destinationCurrencyCode=${allocation.destinationCurrency.code}&allocationId=${allocation.id}`);
  }

  /**
   * Get last column value based on allocation status and if allocated to
   * @returns Last column value
   */
  const getLastColumn = () => {
    if (allocation.bankAccountStatus === BANK_ACCOUNT_STATUS.AVAILABLE) {
      if (allocation.fixed > 0) {
        return Number(allocation.fixed).toLocaleString(navigator.language, { style: "currency", currency: productCurrency.code });
      } else if (allocation.percentage > 0) {
        return `${allocation.percentage}%`;
      } else if (allocation.getsRemainder && isIndexPage) {
        return i18n.t("COMMON.100_PERCENT");
      } else if (allocation.getsRemainder && !isIndexPage) {
        return "Remaining";
      } else {
        return "";
      }
    } else if (allocation.bankAccountStatus === BANK_ACCOUNT_STATUS.UNDER_REVIEW) {
      return i18n.t("COMMON.AVAILABLE_IN_2_3_DAYS");
    } else if (allocation.bankAccountStatus === BANK_ACCOUNT_STATUS.INCOMPLETE) {
      return (
        <Action
          displayType={ACTION_DISPLAY_TYPES.SECONDARY}
          title={i18n.t("COMMON.FIX")}
          className="btn-short"
          onClick={() => handleEditBank()}
        />
      )
    }
  }

  // if card allocation, set toValue to "Card", otherwise set to destination country name
  const toValue = allocation.cardNumberLastFour ? i18n.t("COMMON.CARD") : destinationCountry.name;
  const corridor = `${destinationCurrency.code} to ${toValue}`;

  let fee = allocation.bankAccountStatus === BANK_ACCOUNT_STATUS.AVAILABLE ? wireFee.toLocaleString(navigator.language, { style: "currency", currency: allocation.productCurrency.code }) : "";
  fee = selected || selectedForFreeTransfer ? "Free" : fee;
  const status = getStatus();
  const lastColumn = getLastColumn();
  // if card allocation and not selected, show highlight
  // don't show highlight if selected (i.e. when on confirmation page)
  const showCardHighlight = allocation.cardNumberLastFour && isIndexPage;

  return (
    <tr className={showCardHighlight ? "highlight" : ""}>
      <td>{accountName}</td>
      <td>{name}</td>
      <td>{corridor}</td>
      <td>{fee}</td>
      {showStatusCol && <td>{status}</td>}
      <td className={allocation.bankAccountStatus !== BANK_ACCOUNT_STATUS.AVAILABLE ? "flex-row justify-content-center" : ""}>{lastColumn}</td>
    </tr>
  );
}

export default AllocationTableRow;